/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { DragDropService, LanguageService, LogService, UtilsService } from '../../../../../../core/services';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LbErrorService, LbRbacService } from '../../../services';
import { SSLCiphersApiService } from '../../../http';
import { forkJoin, Observable } from 'rxjs';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-add-cipher-list',
    templateUrl: './addEditCipherList.component.html',
    styleUrls: ['./addEditCipherList.component.scss']
})

export class AddEditCipherListComponent extends BaseComponent implements OnInit {
    /**
     * Store the minimum cipher security version.
     */
    @Input() set minCipherSecVersion(version: string) {
        this._minCipherSecVersion = version;
        this.updateEmbeddedPageData();
    }

    /**
     * Store the maximum cipher security version.
     */
    @Input() set maxCipherSecVersion(version: string) {
        this._maxCipherSecVersion = version;
        this.updateEmbeddedPageData();
    }

    /**
     * Determine if the page is visible. Toggled by parent.
     */
    public addEditPageVisible: boolean = false;

    /**
     * Available ciphers (changes based on cipher dropdown).
     */
    public availableCiphers: any = [];

    /**
     * Ciphers in the right dropzone (changes based on user selection).
     */
    public selectedCiphers: any = [];

    /**
     * Cipher form.
     */
    public ciphersForm: FormGroup;

    /**
     * Filter form.
     */
    public filtersForm: FormGroup;

    /**
     * Used to populate the dropdown.
     */
    public cipherSuiteDropdown = [
        <DropdownModel>{ key: 'All', value: 'All Versions' },
        <DropdownModel>{ key: 'TLSv1.0', value: 'TLSv1.0' },
        <DropdownModel>{ key: 'TLSv1.1', value: 'TLSv1.1' },
        <DropdownModel>{ key: 'TLSv1.2', value: 'TLSv1.2' },
        <DropdownModel>{ key: 'TLSv1.3', value: 'TLSv1.3' }
    ];

    /**
     * Store the temporary cipher suites. This changes based on min/max security settings.
     */
    public tempCipherSuiteDropdown = [<DropdownModel>{ key: 'All', value: 'All Versions' }];

    /**
     * All ciphers grouped by suite name.
     */
    public ciphersBySuiteName = [{
        suiteName: '',
        cipher: ''
    }];

    /**
     * Set the form state to show/hide reactive details.
     */
    public editId: string;

    /**
     * Determine if the drag is active or not.
     */
    public dragActive: boolean = false;

    /**
     * List of all ciphers returned from API.
     */
    public ciphers: any = [];

    /**
     * Flag to check if the data has been retrieved.
     */
    public ciphersRetrieved: boolean = false;

    /**
     * Embedded component flag.
     */
    public componentEmbedded: boolean = false;

    /**
     * Set duplicate mode.
     * @private
     */
    public duplicate: boolean = false;

    /**
     * Store callback function.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store the page title.
     */
    public pageTitle: string;

    /**
     * Store min cipher security version.
     * @private
     */
    private _minCipherSecVersion: string;

    /**
     * Store max cipher security version.
     * @private
     */
    private _maxCipherSecVersion: string;

    /**
     * Store the cipher lists created by the user.
     * @private
     */
    private cipherLists: any = [];

    /**
     * Store the current cipher list when in edit mode.
     * @private
     */
    private currentCipherList: any;

    /**
     * Store the initial form data to compare against form changes.
     * @private
     */
    private initialData: any = {};

    /**
     * Store the security types e.g TLS and their version numbers.
     * @private
     */
    private cipherSecurityVersions: any[] = [];

    constructor(
        public rbacService: LbRbacService,
        private cdr: ChangeDetectorRef,
        private dragDropService: DragDropService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private route: ActivatedRoute,
        private router: Router,
        private sslCipherApiService: SSLCiphersApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageData();

        this.dragDropService.dragActive.pipe(takeUntil(this.destroyed$))
            .subscribe(state => {
                this.dragActive = state;
            });
    }

    /**
     * Get min cipher security version.
     */
    get minCipherSecVersion() {
        return this._minCipherSecVersion;
    }

    /**
     * Get min cipher security version.
     */
    get maxCipherSecVersion() {
        return this._maxCipherSecVersion;
    }

    public ngOnInit(): void {
        this.setUpPageType();
        this.pageTitle = this.editId ? 'cipher_list_edit_cipher' : 'cipher_list_create_new';
    }

    /**
     * Set up page type e.g. add, edit etc.
     * @private
     */
    private setUpPageType(): void {
        const path = this.utilsService.getObjectValue(this.route, 'snapshot.url[0].path');
        this.duplicate = path?.includes('duplicate');
    }

    /**
     * Get data for page.
     * @private
     */
    private setUpPageData(): void {
        forkJoin([
            this.sslCipherApiService.getAllCiphers(),
            this.sslCipherApiService.getAllCipherLists()
        ]).pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.ciphers = data[0];
                    this.cipherLists = data[1];
                    if (this.ciphers.length) {
                        this.setPageData();
                        this.ciphersRetrieved = true;
                    }

                    if (this.componentEmbedded) {
                        this.updateCipherSuiteDropdown();
                    }
                }
            );
    }

    /**
     * Set data for the page.
     * @private
     */
    private setPageData(): void {
        this.availableCiphers = [...this.ciphers];

        this.editId = this.editId || this.route.snapshot.paramMap.get('id');
        if (this.editId) {
            const patchData = this.getFormattedData(this.editId);
            this.patchDataToForm(patchData);
        }
        this.createCipherTooltipLookup();
    }

    /**
     * Update the embedded page with the latest dropdown options.
     * @private
     */
    private updateEmbeddedPageData(): void {
        if (this.addEditPageVisible && this.ciphersRetrieved === true) {
            this.updateCipherSuiteDropdown();
            this.filterResponseByFormValues();
        } else {
            return;
        }
    }

    /**
     * Update cipher suite dropdown.
     * @private
     */
    private updateCipherSuiteDropdown(): void {
        this.tempCipherSuiteDropdown = [];
        this.storeSSLVersionRefs();
        const validProtocols: number[] = this.getValidProtocolVersions();
        for (const protocol of validProtocols) {
            const protocolName: string = 'TLSv' + (protocol.toFixed(1).toString());
            if (!this.tempCipherSuiteDropdown.includes(<DropdownModel>{ key: protocolName, value: protocolName })) {
                this.tempCipherSuiteDropdown.push(<DropdownModel>{
                    key: protocolName,
                    value: protocolName
                });
            }
        }

        if (this.tempCipherSuiteDropdown.length > 1) {
            this.tempCipherSuiteDropdown.unshift(<DropdownModel>{ key: 'All', value: 'All Versions' });
        }
    }

    /**
     * Store the references to the security protocol versions.
     * @private
     */
    private storeSSLVersionRefs(): void {
        for (const suite of this.cipherSuiteDropdown) {
            if (suite.key === 'All') {
                continue;
            }

            const versionNum: number = this.utilsService.getCipherSecurityVersion(suite.key);
            if (!this.cipherSecurityVersions.includes(versionNum)) {
                this.cipherSecurityVersions.push(versionNum);
            }
        }
    }

    /**
     * Get valid protocol versions using the min and max security protocol versions.
     * @private
     */
    private getValidProtocolVersions(): any[] {
        const minVersion: number = this.utilsService.getCipherSecurityVersion(this.minCipherSecVersion);
        const maxVersion: number = this.utilsService.getCipherSecurityVersion(this.maxCipherSecVersion);

        return this.cipherSecurityVersions.filter(item => item >= minVersion && item <= maxVersion);
    }

    /**
     * Patch form data.
     * @param data
     * @private
     */
    private patchDataToForm(data: any[]): void {
        this.ciphersForm.patchValue(data);
        this.initialData = this.ciphersForm.value;
        this.currentCipherList = data;
        this.cdr.detectChanges();
    }

    /**
     * Set data for duplicated cipher list.
     * @data
     * @private
     */
    private getFormattedData(id: string): any {
        const patchData = this.cipherLists.find(item => item.id === id);

        if (this.duplicate) {
            delete patchData.id;
            delete patchData.name;
        }

        return patchData;
    }

    /**
     * Helper function, makes a call to each updateCipherSuiteArray for each suite.
     * @private
     */
    private createCipherTooltipLookup(): void {
        this.updateCipherSuites('TLSv1');
        this.updateCipherSuites('TLSv1.1');
        this.updateCipherSuites('TLSv1.2');
        this.updateCipherSuites('TLSv1.3');
    }

    /**
     * Update table rows with searched rows.
     * @param $event
     */
    public updateAvailableCiphers($event: any): void {
        this.availableCiphers = [...$event];
        this.filterResponseByFormValues([...$event]);
    }

    /**
     * Add the changes to the form.
     * @param event
     */
    public updateSelected(event: any): void {
        this.ciphersForm.get('ciphers').setValue(event);
        this.filterResponseByFormValues();
    }

    /**
     * Filter the response data based on form filters.
     * @param response
     * @private
     */
    public filterResponseByFormValues(response?: any): void {
        const formValues = this.filtersForm?.value;
        if (formValues) {
            const filteredCiphers = [...this.removeSelectedCiphersFromCiphersList()];
            const clonedResponse = response ? this.removeSelectedCiphersFromSearchResults(response) : filteredCiphers;
            this.availableCiphers = [...clonedResponse.filter(item => {
                if (((!this.componentEmbedded && formValues.tlsVersion === 'All') || item.tlsVersion.includes(formValues.tlsVersion)) &&
                    this.matchesSearchTerm(formValues.searchTerm, item)) {
                    return item;
                }

                if (this.componentEmbedded && formValues.tlsVersion === 'All') {
                    if (this.cipherIncludesTLSVersion(item)) {
                        return item;
                    }
                }
            })];
        } else if (this.selectedCiphers.length) {
            this.availableCiphers = [...this.removeSelectedCiphersFromCiphersList()];
        } else {
            this.availableCiphers = [...this.ciphers];
        }

        const securityLevelTimer = setTimeout(() => {
            this.dragDropService.reRunInitialSetup.next(true);
            clearTimeout(securityLevelTimer);
        }, 0);
    }

    /**
     * Check if a cipher contains any of the tls versions in the updated dropdown.
     * @param item
     * @private
     */
    private cipherIncludesTLSVersion(item: any): boolean {
        const ciphersDropdown = this.componentEmbedded ? this.tempCipherSuiteDropdown : this.cipherSuiteDropdown;
        for (const cipher of ciphersDropdown) {
            if (item.tlsVersion.includes(cipher.key)) {
                return true;
            }
        }
    }

    /**
     * Remove selected ciphers, if any, from the output of the search.
     * @param response
     * @private
     */
    private removeSelectedCiphersFromSearchResults(response: any): any[] {
        const selectedNames = this.ciphersForm.get('ciphers').value;

        return selectedNames?.length ? response.filter(item => !selectedNames.includes(item.name)) : response;
    }

    /**
     * Search through the items values for a match to the search terms.
     * @param searchTerm
     * @param item
     * @private
     */
    private matchesSearchTerm(searchTerm: string, item: any): boolean {
        if (searchTerm && searchTerm.length) {
            const matches = [];
            for (const value of Object.values(item)) {
                for (const filter of searchTerm) {
                    const searchText = new RegExp(filter, 'gi');
                    if (value && searchText.test(value.toString())) {
                        matches.push(value);
                    }
                }
            }

            return !!matches.length;
        } else {
            return true;
        }
    }

    /**
     * Remove the selected ciphers from the ciphers list and return result.
     * @private
     */
    private removeSelectedCiphersFromCiphersList(): any[] {
        if (this.selectedCiphers.length) {
            const selectedNames = this.ciphersForm.get('ciphers').value;

            return this.ciphers.filter(item => !selectedNames.includes(item.name));
        }

        return this.ciphers;
    }

    /**
     * Submit either a new or edited cipher to the API.
     */
    public submitCipherList(): Observable<any> {
        return new Observable<any>((observer: any) => {
            const cipherListId: string = this.ciphersForm.get('id').value;
            const ciphersFormValue: any = this.ciphersForm.value;

            const apiCall = this.editId && !this.duplicate
                ? this.sslCipherApiService.editCipherList(ciphersFormValue, cipherListId)
                : this.sslCipherApiService.addCipherList(ciphersFormValue);

            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        if (this.componentEmbedded) {
                            this.callBack();
                        } else {
                            this.router.navigate(
                                [this.editId ? '../../' : '..'],
                                { relativeTo: this.route }).then();
                        }

                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.ciphersForm, error.error.errors);
                        this.logService.error(
                            'submitCipherList: Add/Edit cipher list',
                            'Error submitting data', error);

                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Takes tls-version and pushes it and cipher name to the completeCipherList.
     * @param tlsVersion
     * @private
     */
    private updateCipherSuites(tlsVersion: string): void {
        for (const cipher of this.ciphers) {
            if (cipher.tlsVersion.indexOf(tlsVersion) !== -1) {
                this.ciphersBySuiteName.push({ suiteName: tlsVersion, cipher: cipher.name });
            }
        }
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpForm(): void {
        this.ciphersForm = this.formBuilder.group({
            id: [''], // <-- used in row.
            name: [null, [Validators.required, nameValidation]],
            ciphers: [null, Validators.required],
        });

        this.filtersForm = this.formBuilder.group({
            searchTerm: [null],
            tlsVersion: ['All']
        });
    }

    /**
     * Set search variable in class.
     * @param $event
     */
    public setSearchVariable($event: any): void {
        this.filtersForm.get('searchTerm').setValue($event);
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        const listLabel = this.ciphersForm.get('name');
        listLabel.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.utilsService.debounce('newCiphers', () => {
                        if (this.labelIsDuplicate(value)) {
                            listLabel.setErrors({ 'duplicateLabel': `${this.languageService.translate('duplicate_cipher_list')}` });
                        }
                    }, 250);
                }
            });

        this.ciphersForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        if (this.editId && !this.duplicate) {
                            if (!this.utilsService.hasFormDataChanged(this.initialData, form)) {
                                this.ciphersForm.markAsPristine();
                            } else {
                                this.ciphersForm.markAsDirty();
                            }
                        }
                    });
                }
            });

        this.ciphersForm.get('ciphers').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.selectedCiphers = values;
                }
            });

        this.filtersForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.filterResponseByFormValues();
                    this.cdr.detectChanges();
                }
            });
    }

    /**
     * Validate cipher list label.
     * @param value
     * @private
     */
    private labelIsDuplicate(value: string): boolean {
        const matchFound = this.cipherLists.find(item => item.name === value);

        return this.editId ? matchFound && matchFound.name !== this.currentCipherList.name : matchFound;
    }

    /**
     * Returns suite name for any given cipher.
     * @param currentCipher
     */
    public getSuiteName(currentCipher: string): string {
        // Reset suiteNamesString with prefix.
        let suiteNamesString = 'This cipher exists in: ';
        const matches = this.ciphersBySuiteName.filter(item => item.cipher === currentCipher);

        for (const match of matches) {
            suiteNamesString = suiteNamesString + ', ' + match.suiteName;
        }

        return suiteNamesString.replace(/,/g, '');
    }

    /**
     * Get security level from cipher name.
     * @param name
     */
    public getSecurityLevel(name: string): string {
        const cipher = this.ciphers.find(item => item.name === name);

        return cipher.security;
    }

    /**
     * Cancel cipher add/edit.
     */
    public goBack(): void {
        if (this.componentEmbedded) {
            this.callBack();
        } else {
            this.router.navigate([this.editId ? '../..' : '..'], { relativeTo: this.route }).then();
        }
    }
}
